import { useEffect, useRef, useState } from 'react';
import { ENTER_KEY } from 'constants/keyCodes';
import { colors } from 'ui/theme';
import { Truncate } from 'ui/utility/truncate';
import styled from 'styled-components';
import { subtitle1, heading4 } from 'ui/theme/typography';
import noop from 'utils/noop';
import {
  EditIcon,
  EditMode,
  NodeTitleText,
  NodeTitleTextDisplay,
  FullWidthTextInput,
  StyledTooltip,
} from './EditableTextInput.styled';

// type Props = {
//   /** True if the text input is editable */
//   isEditable?: boolean,
//   /** Initial value of text displayed in read-only state */
//   title?: string,
//   /** Callback that saves current input in edit mode */
//   onInputSave: (editableNodeTitle: string) => void,
//   /** Specifes the number of characters that can be entered in the input  */
//   maxLength?: string | number,
//   /** If provided, will truncate text in pixels and show tooltip  */
//   maxWidth?: number,
//   /** Specifes a class added to node title  */
//   className?: string,
// };

export const NODE_EDITOR_HEADER_TITLE = 'node-editor-header-title';
export const NODE_TITLE_DISPLAY_TEXT = 'node-title-display-text';
export const NODE_TITLE_EDIT_ICON = 'node-title-edit-icon';

// Function `EditableTextInput` has a Cognitive Complexity of 15 (exceeds 10 allowed). Consider refactoring. …
/**
 * EditableTextInput
 * @param {Props} props
 */
export const EditableTextInput = ({
  onInputSave = noop,
  isEditable = true,
  title,
  maxLength,
  maxWidth,
  className,
}) => {
  const inputRef = useRef(null);
  const titleRef = useRef(null);
  const wrapperRef = useRef(null);

  const isTruncated =
    !!maxWidth && wrapperRef?.current?.offsetWidth < titleRef?.current?.scrollWidth;
  const [editableNodeTitle, setEditableNodeTitle] = useState(title);
  const [nodeTitleEditMode, setNodeTitleEditMode] = useState(false);

  useEffect(() => {
    if (title) setEditableNodeTitle(title);
  }, [title]);

  useEffect(() => {
    if (nodeTitleEditMode) {
      inputRef.current?.focus();
    }
  }, [nodeTitleEditMode]);

  const handleSaveInput = (event) => {
    const { type, key, which } = event;
    if (type === 'blur' || (type === 'keydown' && (which === ENTER_KEY || key === ENTER_KEY))) {
      setNodeTitleEditMode(false);
      if (!editableNodeTitle) setEditableNodeTitle(title);
      onInputSave(editableNodeTitle);
    }
  };

  const handleExitInput = (event) => {
    const { type, key, which } = event;
    if (type === 'click' || (type === 'keydown' && (which === ENTER_KEY || key === ENTER_KEY))) {
      setNodeTitleEditMode(true);
    }
  };

  const TitleComponent = maxWidth ? Truncate : NodeTitleTextDisplay;

  return (
    <NodeTitleText className={className} data-test={NODE_EDITOR_HEADER_TITLE} ref={wrapperRef}>
      {nodeTitleEditMode ? (
        <EditMode>
          <FullWidthTextInput
            maxLength={maxLength}
            type="text"
            onBlur={handleSaveInput}
            onKeyDown={handleSaveInput}
            onChange={setEditableNodeTitle}
            inputRef={inputRef}
            value={editableNodeTitle}
          />
        </EditMode>
      ) : (
        <>
          <StyledTooltip tooltipContent={editableNodeTitle} isEnabled={isTruncated}>
            <TitleComponent data-test={NODE_TITLE_DISPLAY_TEXT} maxWidth={maxWidth} ref={titleRef}>
              {editableNodeTitle}
            </TitleComponent>
          </StyledTooltip>
          {!!isEditable && (
            <EditIcon
              data-test={NODE_TITLE_EDIT_ICON}
              isColorInherited={true}
              onClick={handleExitInput}
              onKeyDown={handleExitInput}
              tabIndex="0"
              title="Edit title"
            />
          )}
        </>
      )}
    </NodeTitleText>
  );
};

export const EditableTileHeader = styled(EditableTextInput)`
  ${subtitle1};
  color: ${colors.textSecondary};
`;

export const EditableStudioHeader = styled(EditableTextInput)`
  ${heading4};
  color: ${({ $showPublishedHeader } /*: { $showPublishedHeader: boolean } */) =>
    $showPublishedHeader ? colors.textPrimary : colors.white};
`;
